/**
 * Light-weight markdown renderer for Forge Ledger page copy.
 * Supports the small subset we use in editable page content:
 *   - **bold** → <strong>
 *   - *italic* / _italic_ → <em>
 *   - [text](url) → <a> (only http/https allowed)
 *
 * Even though page content is only writable by staff/admin (server-side
 * enforced), we still pipe every render through DOMPurify so a compromised
 * staff account or future content surface can't smuggle a <script>.
 */
import React, { useMemo } from 'react';
import DOMPurify from 'dompurify';

const escapeHtml = (s) => s
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;');

const inlineToHtml = (raw) => {
  let s = escapeHtml(raw);
  // Bold (must run before italic so **x** wins over *x*).
  s = s.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
  // Italic — both _x_ and *x*.
  s = s.replace(/(^|[^*])\*(?!\s)([^*]+?)\*/g, '$1<em>$2</em>');
  s = s.replace(/(^|[^_])_(?!\s)([^_]+?)_/g, '$1<em>$2</em>');
  // Links — restricted to http(s) so javascript: URIs can't slip through.
  s = s.replace(/\[([^\]]+)\]\((https?:[^)\s]+)\)/g,
    '<a href="$2" target="_blank" rel="noopener noreferrer" class="text-[#0052FF] underline">$1</a>');
  return s;
};

// Restrict to the inline subset we actually use. DOMPurify will strip anything else.
const SANITIZE_CONFIG = {
  ALLOWED_TAGS: ['strong', 'em', 'a', 'br'],
  ALLOWED_ATTR: ['href', 'target', 'rel', 'class'],
  ALLOW_DATA_ATTR: false,
};

const sanitize = (html) => DOMPurify.sanitize(html, SANITIZE_CONFIG);

export const RenderMarkdownInline = ({ text }) => {
  const safe = useMemo(() => (text ? sanitize(inlineToHtml(text)) : ''), [text]);
  if (!text) return null;
  return <span dangerouslySetInnerHTML={{ __html: safe }} />;
};

export const RenderMarkdownBlock = ({ text, className }) => {
  const paragraphs = useMemo(() => {
    if (!text) return [];
    // Each paragraph keyed by a stable content hash so React reuses nodes correctly
    // when copy is edited rather than rebuilding the subtree.
    return text.split(/\n{2,}/).map((p, i) => ({
      key: `p-${i}-${p.length}-${p.slice(0, 24)}`,
      html: sanitize(inlineToHtml(p)),
    }));
  }, [text]);
  if (!text) return null;
  return (
    <>
      {paragraphs.map((p) => (
        <p key={p.key} className={className} dangerouslySetInnerHTML={{ __html: p.html }} />
      ))}
    </>
  );
};

// ---------------------------------------------------------------------------
// RenderMarkdownArticle — richer renderer for long-form blog content.
// Adds headings (## / ###), unordered lists, blockquotes, and ``` code blocks
// on top of the inline subset (bold/italic/links).
// ---------------------------------------------------------------------------

const ARTICLE_SANITIZE = {
  ALLOWED_TAGS: [
    'strong', 'em', 'a', 'br', 'h2', 'h3', 'p', 'ul', 'ol', 'li',
    'blockquote', 'pre', 'code', 'hr',
    'table', 'thead', 'tbody', 'tr', 'th', 'td',
  ],
  ALLOWED_ATTR: ['href', 'target', 'rel', 'class'],
  ALLOW_DATA_ATTR: false,
};

const sanitizeArticle = (html) => DOMPurify.sanitize(html, ARTICLE_SANITIZE);

const articleToHtml = (raw) => {
  // Fenced code blocks first — protect their content from further substitutions.
  const codeBlocks = [];
  let s = raw.replace(/```([\s\S]*?)```/g, (_, code) => {
    codeBlocks.push(code.trim());
    return `\u0000CODE${codeBlocks.length - 1}\u0000`;
  });

  s = s.split(/\n{2,}/).map((block) => {
    block = block.trim();
    if (!block) return '';
    if (block.startsWith('\u0000CODE')) {
      const idx = Number(block.match(/\u0000CODE(\d+)\u0000/)?.[1]);
      const code = escapeHtml(codeBlocks[idx] || '');
      return `<pre><code>${code}</code></pre>`;
    }
    if (block.startsWith('### ')) {
      return `<h3 class="text-lg font-bold mt-8 mb-3 text-[#020617]">${inlineToHtml(block.slice(4))}</h3>`;
    }
    if (block.startsWith('## ')) {
      return `<h2 class="text-2xl font-bold mt-10 mb-4 text-[#020617]">${inlineToHtml(block.slice(3))}</h2>`;
    }
    if (block.startsWith('> ')) {
      const quote = block.split('\n').map((l) => l.replace(/^>\s?/, '')).join(' ');
      return `<blockquote class="border-l-4 border-[#DBEAFE] pl-4 italic text-[#475569] my-6">${inlineToHtml(quote)}</blockquote>`;
    }
    // Horizontal rule — a line of only --- or *** on its own.
    if (/^(-{3,}|\*{3,})$/.test(block)) {
      return '<hr class="my-10 border-t border-[#E2E8F0]" />';
    }
    // GitHub-flavoured table — first line is the header row, second is the
    // `| --- | --- |` separator, remaining lines are the body rows.
    // We're forgiving about leading/trailing pipes and column count drift.
    const lines = block.split('\n');
    if (
      lines.length >= 2 &&
      lines[0].includes('|') &&
      /^\s*\|?\s*:?-{2,}/.test(lines[1])
    ) {
      const splitRow = (row) => row
        .replace(/^\s*\|/, '')
        .replace(/\|\s*$/, '')
        .split('|')
        .map((c) => c.trim());
      const header = splitRow(lines[0]);
      const bodyRows = lines.slice(2).filter((l) => l.includes('|')).map(splitRow);
      const thead = `<thead><tr>${
        header.map((h) => `<th class="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wider text-[#64748B] border-b border-[#E2E8F0] bg-[#F8FAFC]">${inlineToHtml(h)}</th>`).join('')
      }</tr></thead>`;
      const tbody = `<tbody>${
        bodyRows.map((row) => `<tr>${
          row.map((c) => `<td class="px-3 py-2 text-sm text-[#334155] border-b border-[#E2E8F0] align-top">${inlineToHtml(c)}</td>`).join('')
        }</tr>`).join('')
      }</tbody>`;
      return `<div class="my-6 overflow-x-auto"><table class="w-full border-collapse border border-[#E2E8F0] rounded-lg overflow-hidden">${thead}${tbody}</table></div>`;
    }
    // Ordered list — lines like "1. Foo" / "2. Bar".
    if (/^\d+\.\s/.test(block)) {
      const items = block.split('\n')
        .filter((l) => /^\d+\.\s/.test(l))
        .map((l) => `<li>${inlineToHtml(l.replace(/^\d+\.\s/, ''))}</li>`)
        .join('');
      return `<ol class="list-decimal list-outside pl-6 my-4 space-y-2 text-[#334155]">${items}</ol>`;
    }
    if (/^[-*]\s/.test(block)) {
      const items = block.split('\n')
        .filter((l) => /^[-*]\s/.test(l))
        .map((l) => `<li>${inlineToHtml(l.replace(/^[-*]\s/, ''))}</li>`)
        .join('');
      return `<ul class="list-disc list-outside pl-6 my-4 space-y-2 text-[#334155]">${items}</ul>`;
    }
    return `<p class="my-4 leading-relaxed text-[#334155]">${inlineToHtml(block)}</p>`;
  }).join('\n');

  return s;
};

export const RenderMarkdownArticle = ({ text, className }) => {
  const safe = useMemo(() => (text ? sanitizeArticle(articleToHtml(text)) : ''), [text]);
  if (!text) return null;
  return <div className={className} dangerouslySetInnerHTML={{ __html: safe }} />;
};

export default RenderMarkdownBlock;
